import React, { useEffect, useMemo } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Callout,
  Checkbox,
  Input2FA,
  RowsSkeletonLoader,
} from '@node-space/ui-components'
import { ExternalLink } from 'components/ExternalLink/ExternalLink'
import { TWO_FA_REQUIRED_LENGTH } from 'components/TwoFA/TwoFAField'
import { BVNK_BLOG_FRAUD_PREVENTION } from 'constants/General'
import { useAddBeneficiaryContext } from 'hooks/useAddBeneficiaryContext'
import { getConfirmPayeeBeneficiaryParams } from 'pages/Contacts/utils/fiatInstruments'
import { ConfirmationOfPayeeCallout } from 'pages/Wallets/components/FiatSend/components/ConfirmationOfPayeeCallout'
import { useConfirmBeneficiaryCallout } from 'pages/Wallets/components/FiatSend/hooks/useConfirmBeneficiaryCallout'
import { ConfirmationOfPayeeCalloutTypes } from 'pages/Wallets/components/FiatSend/types'
import {
  AddBeneficiaryFormPayload,
  AddLocalBeneficiaryFormConfirmPayload,
} from 'types/beneficiaries'
import { ConfirmPayeeBeneficiaryParams, ConfirmPayeeMatchStatuses } from 'types/payoutsV2'
import { Currencies } from 'types/types'
import { orchestrateAddBeneficiaryPayload } from 'utils/beneficiaries'
import { sanitizeValues } from 'utils/utils'
import { ModalStep } from '../AddBeneficiary'
import { BeneficiarySummary } from './BeneficiarySummary'
import { addBeneficiaryConfirmSchema } from './schemas/addBeneficiaryConfirmSchema'

type AddBeneficiaryConfirmProps = {
  error: boolean
  isLoading: boolean
  onContinue: (password: string) => void
  setStep: (step: ModalStep) => void
}

export const AddBeneficiaryConfirm = ({
  error,
  isLoading,
  onContinue,
  setStep,
}: AddBeneficiaryConfirmProps) => {
  const { t } = useTranslation()
  const { beneficiaryFormState, beneficiaryFormErrors } = useAddBeneficiaryContext()

  const formSchema = addBeneficiaryConfirmSchema()
  const { control, register, setValue, setError, clearErrors } =
    useForm<AddLocalBeneficiaryFormConfirmPayload>({
      resolver: yupResolver(formSchema),
    })

  const beneficiaryDetails: ConfirmPayeeBeneficiaryParams = useMemo(() => {
    const orchestratedBeneficiaryDetails = orchestrateAddBeneficiaryPayload(
      sanitizeValues(beneficiaryFormState) as AddBeneficiaryFormPayload
    )

    return getConfirmPayeeBeneficiaryParams(orchestratedBeneficiaryDetails)
  }, [beneficiaryFormState])

  const { confirmPayeeData, confirmationOfPayeeCallout, isConfirmingPayee } =
    useConfirmBeneficiaryCallout({
      beneficiaryDetails,
      isPayout: false,
    })

  const isGBPBeneficiary = beneficiaryFormState?.currencyCode === Currencies.GBP

  const [password, isDetailsCorrect] = useWatch({
    control,
    name: ['password', 'isDetailsCorrect'],
    defaultValue: {
      password: '',
      isDetailsCorrect: false,
    },
  })

  useEffect(() => {
    if (beneficiaryFormErrors?.password) {
      setError('password', beneficiaryFormErrors?.password)
    } else {
      clearErrors()
    }
  }, [setError, clearErrors, beneficiaryFormErrors?.password])

  useEffect(() => {
    if (password?.length === TWO_FA_REQUIRED_LENGTH) {
      onContinue(password)
    }
  }, [password])

  const canEditDetails = useMemo(() => {
    const editEnabledForStatuses = [
      ConfirmPayeeMatchStatuses.NO_MATCH,
      ConfirmPayeeMatchStatuses.PARTIAL_MATCH,
    ]

    return editEnabledForStatuses.includes(confirmPayeeData?.matchStatus)
  }, [confirmPayeeData?.matchStatus])

  const handleEditDetails = () => {
    setStep(ModalStep.ADD_BENEFICIARY)
  }

  return isConfirmingPayee ? (
    <Box paddingT={24}>
      <RowsSkeletonLoader rows={6} />
    </Box>
  ) : (
    <Box flex flexCol padding={24} paddingT={20}>
      <BeneficiarySummary />

      {isGBPBeneficiary && (
        <>
          <Box paddingT={24} paddingB={8}>
            <Callout
              variant="info"
              GhostButton={
                <Button onClick={() => window.open(BVNK_BLOG_FRAUD_PREVENTION, '_blank')}>
                  {t('howToPaySafelyMessage')}
                </Button>
              }
            >
              {t('authorisedPushPaymentsMessage')}
            </Callout>
          </Box>
          <Box paddingT={12} paddingB={8}>
            <Checkbox
              {...register('isDetailsCorrect')}
              id="beneficiary-checkbox"
              testId="beneficiary-checkbox"
              checked={isDetailsCorrect}
              onCheckedChange={() => setValue('isDetailsCorrect', !isDetailsCorrect)}
              label={t('authorisedPushPaymentsConfirmationMessage')}
            >
              <ExternalLink className="whitespace-nowrap" href={BVNK_BLOG_FRAUD_PREVENTION}>
                {t('learnMore')}
              </ExternalLink>
            </Checkbox>
          </Box>
        </>
      )}

      <Box flexCol width="full" paddingT={24} paddingB={12} gapY={16}>
        <Box flexCol justify="between" width="full" gapY={16}>
          {confirmationOfPayeeCallout && (
            <ConfirmationOfPayeeCallout
              type={ConfirmationOfPayeeCalloutTypes.BENEFICIARY}
              confirmationOfPayeeCallout={confirmationOfPayeeCallout}
              confirmPayeeData={confirmPayeeData}
              editBeneficiary={canEditDetails}
              handleEditBeneficiary={handleEditDetails}
            />
          )}
        </Box>

        <Input2FA
          {...register('password')}
          label={t('authCodeConfirm')}
          onChange={value => setValue('password', value)}
          testId="input-2fa"
          error={error && t('incorrectOtpCode')}
          disabled={!isDetailsCorrect && isGBPBeneficiary}
          loading={isLoading}
        />
      </Box>
    </Box>
  )
}
